import { type Project } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { Tooltip } from '@/components/ui';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { EntityIcon } from '@/components/ui/icon-picker';
import { RoundIconButton } from '@/design-system-v2/round-icon-button';
import {
  ProjectDropdownItems,
  useProjectsMenuScrollLoadMore,
} from '@/features/projects/move-to-project';
import { type ProjectMenuOption } from '@/features/projects/project-menu-option';
import { useShortcutLabel } from '@/features/shortcuts';
import { MAX_ATTACHMENTS_PER_MESSAGE } from '@/utils/constants';

import { ChatEngineMenuItem } from './ChatEngineMenuItem';
import {
  IntegrationsMenuItems,
  useArgonChatModeGuard,
  useGodChatModeGuard,
  useTeamChatModeGuard,
  useUltraChatModeGuard,
} from './ChatModeButtons';
import { ScreenContextMenuItem } from './ScreenContextMenuItem';

type ComposerAddMenuProps = {
  draftId: string;
  canAttachFile: boolean;
  attachmentsCount: number;
  selectedProject: Project | null;
  projectOptions: ProjectMenuOption[];
  onAddFiles: () => void;
  onSelectProject: (projectId: string | null) => Promise<void> | void;
  onCreateProject: () => void;
  hideProjectSelector?: boolean;
  /** An unsent New Chat, the only chat that can still pick its engine */
  isNewChat?: boolean;
  triggerClassName?: string;
};

export const ComposerAddMenu = observer(function ComposerAddMenu({
  draftId,
  canAttachFile,
  selectedProject,
  projectOptions,
  onAddFiles,
  onSelectProject,
  onCreateProject,
  hideProjectSelector,
  isNewChat,
  triggerClassName,
}: ComposerAddMenuProps) {
  const onProjectsMenuScroll = useProjectsMenuScrollLoadMore();
  useUltraChatModeGuard();
  useGodChatModeGuard();
  useArgonChatModeGuard();
  useTeamChatModeGuard();
  const attachShortcut = useShortcutLabel('chat.attachFile');

  const attachFileTooltip = canAttachFile
    ? ''
    : `You can only attach up to ${MAX_ATTACHMENTS_PER_MESSAGE} files per message`;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <RoundIconButton
          id="attach-file-button"
          icon={Icons.attachment}
          size="sm"
          label="Add files and more…"
          className={triggerClassName}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        align="start"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <Tooltip label={attachFileTooltip}>
          <span className="block">
            <DropdownMenu.Item onClick={onAddFiles} disabled={!canAttachFile}>
              <Icons.paperclip className="size-3.5 opacity-75" />
              Add files or photos
              {attachShortcut && (
                <DropdownMenu.Shortcut>{attachShortcut}</DropdownMenu.Shortcut>
              )}
            </DropdownMenu.Item>
          </span>
        </Tooltip>
        {!hideProjectSelector && (
          <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger>
              {selectedProject ? (
                <EntityIcon icon={selectedProject.icon} muted />
              ) : (
                <Icons.folderPlus className="size-3.5 opacity-75" />
              )}
              Add to project
            </DropdownMenu.SubTrigger>
            <DropdownMenu.SubContent onScroll={onProjectsMenuScroll}>
              <ProjectDropdownItems
                projectOptions={projectOptions}
                onCreateProject={onCreateProject}
                onSelectProject={(id) => {
                  void onSelectProject(selectedProject?.id === id ? null : id);
                }}
                onClearProject={() => {
                  void onSelectProject(null);
                }}
              />
            </DropdownMenu.SubContent>
          </DropdownMenu.Sub>
        )}
        <ScreenContextMenuItem
          draftId={draftId}
          selectedProject={selectedProject}
        />
        {isNewChat && <ChatEngineMenuItem draftId={draftId} />}
        <IntegrationsMenuItems />
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
});
